import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  CarrierAvailability,
  CarrierEntry,
  DefaultCarrierConfig,
} from '@/components/carrier/DefaultCarrierConfig';
import {
  extractCarrierErrorMessage,
  withoutUnsupportedServices,
} from '@/components/carrier/carrierErrors';
import { adminInfoStoreQuery } from '../../graphql/queries';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import i18n from '@/utils/i18n';

type Props = {
  storeId: string;
  vytalStoreGuid: string;
  title: string;
};

function sortedKey(entries: CarrierEntry[]): string {
  return JSON.stringify(
    [...entries].sort((a, b) =>
      a.carrier_service.localeCompare(b.carrier_service),
    ),
  );
}

export function StoreCarrierSettingsTab({ storeId, title }: Props) {
  const { data, loading } = useQuery(adminInfoStoreQuery, {
    variables: { id: storeId },
  });

  const store = data?.store ?? null;

  if (loading || !store) {
    return (
      <div className="tw:flex tw:justify-center tw:p-6">
        <Spinner className="tw:size-6" />
      </div>
    );
  }

  return (
    <StoreCarrierSettingsEditor storeId={storeId} store={store} title={title} />
  );
}

type EditorProps = {
  storeId: string;
  store: {
    default_carriers?: CarrierEntry[] | null;
    effective_default_carriers?: CarrierEntry[] | null;
    available_carrier_services?: CarrierAvailability[] | null;
  };
  title: string;
};

function StoreCarrierSettingsEditor({ storeId, store, title }: EditorProps) {
  const initial: CarrierEntry[] = useMemo(
    () =>
      withoutUnsupportedServices(
        (store.default_carriers ?? []).map((d) => ({
          carrier_service: d.carrier_service,
          carrier: d.carrier,
        })),
      ),
    [store.default_carriers],
  );

  const [value, setValue] = useState<CarrierEntry[]>(initial);
  const [initialSnapshot, setInitialSnapshot] =
    useState<CarrierEntry[]>(initial);

  const [updateStore, { loading: saving }] = useMutation(
    updateStoreSuperAdminMutation,
  );

  const isDirty = useMemo(
    () => sortedKey(value) !== sortedKey(initialSnapshot),
    [value, initialSnapshot],
  );

  const availableCarrierServices: CarrierAvailability[] | undefined =
    store.available_carrier_services?.map((a) => ({
      carrier: a.carrier,
      services: a.services,
    })) ?? undefined;

  const effectiveDefaults: CarrierEntry[] | undefined =
    store.effective_default_carriers?.map((d) => ({
      carrier_service: d.carrier_service,
      carrier: d.carrier,
    })) ?? undefined;

  const handleSave = async () => {
    try {
      await updateStore({
        variables: {
          id: storeId,
          store: {
            default_carriers: value.map((d) => ({
              carrier_service: d.carrier_service,
              carrier: d.carrier,
            })),
          },
        },
      });
      setInitialSnapshot(value);
      toast.success(i18n.t('Carrier:saveSuccess'));
    } catch (err) {
      toast.error(extractCarrierErrorMessage(err));
    }
  };

  const handleReset = () => {
    setValue(initialSnapshot);
  };

  return (
    <div className="tw:p-4 tw:flex tw:flex-col tw:gap-4 tw:max-w-3xl">
      <div>
        <h3 className="tw:text-lg tw:font-medium">{title}</h3>
        <p className="tw:text-muted-foreground tw:text-sm tw:mt-1">
          {i18n.t('Carrier:storeSettingsIntro')}
        </p>
      </div>

      <DefaultCarrierConfig
        value={value}
        onChange={setValue}
        availableCarrierServices={availableCarrierServices}
        effectiveDefaults={effectiveDefaults}
      />

      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
        {isDirty && (
          <span className="tw:text-amber-600 tw:text-sm tw:mr-auto">
            {i18n.t('Carrier:unsavedChanges')}
          </span>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={!isDirty || saving}
        >
          {i18n.t('Common:cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? <Spinner /> : null}
          {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
        </Button>
      </div>
    </div>
  );
}
